import React, { ReactNode, useState, useEffect } from 'react';
import styled from 'astroturf/react';
import { WhiteCheckIcon, InfoIcon } from 'js/social/shared/Icons';

export interface SnackbarProps {
  /** Message to be displayed in the snackbar */
  message: ReactNode;
  /** Type of snackbar to be displayed, default is 'info' */
  type?: 'success' | 'warning' | 'failure' | 'info';
  /** If provided, the snackbar becomes a clickable button */
  onClick?: () => void;
  /** Duration in milliseconds for the snackbar to be displayed, default is 3000 */
  durationInMs?: number;
  /** Whether to show an icon at the beginning of the message (if applicable), default is true */
  showIcon?: boolean;
}

const SnackBarContainer = styled(({ onClick, children, ...props }) =>
  onClick ? (
    <button onClick={onClick} {...props}>
      {children}
    </button>
  ) : (
    <div {...props}>{children}</div>
  ),
)`
  display: flex;
  align-items: center;
  justify-content: center;

  border: 1px solid;
  box-shadow:
    0px 6px 10px rgba(0, 0, 0, 0.07),
    0px 1px 18px rgba(0, 0, 0, 0.06),
    0px 3px 5px rgba(0, 0, 0, 0.1);
  border-radius: 7px;

  position: fixed;
  top: calc(var(--nav-mobile-height) + 14px);
  @include for-tablet-up {
    top: calc(var(--nav-desktop-height) + 14px);
  }
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;

  min-height: 56px;
  min-width: 300px;
  padding: 6px 24px;
`;

const SnackBarText = styled.div`
  @import 'stylesheets/design_system/font';
  @include body__medium-medium;

  display: flex;
  align-items: center;
`;

const SnackBar = ({
  message,
  type = 'info',
  onClick = undefined,
  durationInMs = 3000,
  showIcon = true,
}: SnackbarProps) => {
  const [open, setOpen] = useState<boolean>(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setOpen(false);
    }, durationInMs);
    return () => {
      clearTimeout(timer);
    };
  }, [durationInMs]);

  const getColors = (type: SnackbarProps['type']) => {
    switch (type) {
      case 'success':
        return {
          backgroundColor: '#F0F8F1',
          borderColor: '#048C16',
          textColor: '#035C0F',
          icon: <WhiteCheckIcon style={{ marginRight: '6px' }} />,
        };
      case 'warning':
        return {
          backgroundColor: '#F9F6ED',
          borderColor: '#B08920',
          textColor: '#222233',
        };
      case 'failure':
        return {
          backgroundColor: '#FBF0F0',
          borderColor: '#B60000',
          textColor: '#222233',
        };
      default:
        return {
          backgroundColor: '#F4F7FC',
          borderColor: '#0057EB',
          textColor: '#0645B1',
          icon: <InfoIcon style={{ marginRight: '6px' }} />,
        };
    }
  };

  const { backgroundColor, borderColor, textColor, icon } = getColors(type);

  return (
    <div>
      {open && (
        <SnackBarContainer
          style={{
            backgroundColor: backgroundColor,
            border: `1px solid ${borderColor}`,
            color: textColor,
          }}
          onClick={onClick}
        >
          <SnackBarText>
            {showIcon && icon}
            {message}
          </SnackBarText>
        </SnackBarContainer>
      )}
    </div>
  );
};

export default SnackBar;
